import { useCallback, useRef, useState } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────

export type ComplianceStage =
  | 'idle'
  | 'company_extraction'
  | 'obligation_extraction'
  | 'obligation_merge'
  | 'priority_filter'
  | 'obligation_comparison'
  | 'question_merge'
  | 'document_resolution'
  | 're_extraction'
  | 'awaiting_hitl'
  | 'obligation_analysis'
  | 'score_aggregation'
  | 'done'
  | 'error';

export interface AreaProcessed {
  area: string;
  cached: boolean;
}

export interface StepperNode {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'complete';
}

export interface ComplianceQuestion {
  question_id: string;
  dimension_id: string;
  policy_id: string;
  element_id?: string;
  question_text: string;
  question_type?: 'document_request' | 'standalone_question' | null;
  document_type?: string | null;
  document_description?: string | null;
  resolves_question_ids?: string[] | null;
  resolves_dimension_ids?: string[] | null;
}

export interface ObligationStatusLite {
  obligation_id: string;
  policy_id?: string | null;
  article_id?: string | null;
  description?: string | null;
  domain: string | null;
  severity: string | null;
  status: 'compliant' | 'partial' | 'non_compliant';
  reason: string | null;
  gap: string | null;
  remediation: string | null;
  evidence_refs: string[] | null;
  score_contribution?: number;
}

export interface RemediationItemLite {
  priority: number;
  obligationId: string;
  policyName: string;
  articleReference: string;
  gapDescription: string;
  recommendedAction: string;
  estimatedEffort: string;
  deadlineSuggestion: string;
  // Recency of the source policy this remediation is based on (ISO date, may be null).
  policyLastUpdated?: string | null;
}

export interface PolicyCoverage {
  raw_dimensions: number;
  covered: number;
  met: number;
  partial: number;
  missing: number;
}

export interface MergeStats {
  raw_count: number;
  post_dedup_count: number;
  post_filter_count?: number;
  deferred_count?: number;
  per_domain_counts?: Record<string, number>;
  deferred_by_domain?: Record<string, number>;
}

export interface ComplianceFinalResult {
  company_id: string;
  session_id: string;
  engine: string;
  overall_score: number;
  health_rating: string;
  /** Backend sets this when zero applicable obligations were found. The score is
   *  then a placeholder 100/green ("nothing to fail against") — NOT evidence of
   *  compliance — so the UI must say so rather than showing an all-clear. */
  no_applicable_obligations?: boolean;
  status_note?: string | null;
  per_policy_scores: Record<string, {
    score: number;
    compliant: number;
    partial: number;
    non_compliant: number;
    total: number;
    policy_name?: string;
  }>;
  per_domain_scores: Record<string, {
    score: number;
    compliant: number;
    partial: number;
    non_compliant: number;
    total: number;
    label?: string;
  }>;
  obligations_compliant: number;
  obligations_partial: number;
  obligations_non_compliant: number;
  total_obligations: number;
  applicability_results?: any[];
  obligations?: any[];
  obligation_statuses: ObligationStatusLite[];
  remediation_plan: { items: RemediationItemLite[]; total_items: number; generated_at?: string };
  critical_gaps?: ObligationStatusLite[];
  high_gaps?: ObligationStatusLite[];
  merge_stats?: MergeStats;
  deferred_count?: number;
  hitl_iterations?: number;
  assessed_at: string;
}

export interface NodeStat {
  done: number;
  total: number;
  extra?: Record<string, number | string>;
}

export interface ComplianceSocketState {
  stage: ComplianceStage;
  stageMessage: string;
  sessionId: string;
  stepperNodes: StepperNode[];
  areasProcessed: AreaProcessed[];
  dimensionsExtracted: number;
  comparisonProgress: string;
  totalDimensions: number;
  questionsCount: number;
  questions: ComplianceQuestion[];
  finalResult: ComplianceFinalResult | null;
  error: string | null;
  eventLog: string[];
  nodeStats: Record<string, NodeStat>;
}

const DEFAULT_STEPPER: StepperNode[] = [
  { id: 'company_extraction', label: 'Extracting company compliance evidence', status: 'pending' },
  { id: 'obligation_extraction', label: 'Extracting compliance obligations', status: 'pending' },
  { id: 'obligation_merge', label: 'Deduplicating obligations', status: 'pending' },
  { id: 'priority_filter', label: 'Filtering by priority', status: 'pending' },
  { id: 'obligation_comparison', label: 'Running comparisons', status: 'pending' },
  { id: 'question_merge', label: 'Consolidating questions', status: 'pending' },
  { id: 'document_resolution', label: 'Resolving via documents', status: 'pending' },
  { id: 're_extraction', label: 'Auto-extracting answers', status: 'pending' },
  { id: 'hitl', label: 'Awaiting questionnaire', status: 'pending' },
  { id: 'obligation_analysis', label: 'Analysing results', status: 'pending' },
  { id: 'score_aggregation', label: 'Calculating scores', status: 'pending' },
];

const INITIAL_STATE: ComplianceSocketState = {
  stage: 'idle',
  stageMessage: '',
  sessionId: '',
  stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })),
  areasProcessed: [],
  dimensionsExtracted: 0,
  comparisonProgress: '',
  totalDimensions: 0,
  questionsCount: 0,
  questions: [],
  finalResult: null,
  error: null,
  eventLog: [],
  nodeStats: {},
};

const API_BASE = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const WS_BASE = API_BASE.replace(/^http/, 'ws');
const MAX_LOG = 5;

const updateStepper = (nodes: StepperNode[], nodeId: string, status: 'running' | 'complete'): StepperNode[] =>
  nodes.map(n => n.id === nodeId ? { ...n, status } : n);

const addLog = (logs: string[], msg: string): string[] =>
  [...logs.slice(-(MAX_LOG - 1)), msg];

const handleMessage = (msg: any, prev: ComplianceSocketState): ComplianceSocketState => {
  const event = msg.event as string;
  switch (event) {
    case 'session':
      return { ...prev, sessionId: msg.session_id };

    case 'node_started': {
      const nodeId = msg.node as string;
      const stageMap: Record<string, ComplianceStage> = {
        company_extraction: 'company_extraction',
        obligation_extraction: 'obligation_extraction',
        obligation_merge: 'obligation_merge',
        priority_filter: 'priority_filter',
        obligation_comparison: 'obligation_comparison',
        question_merge: 'question_merge',
        document_resolution: 'document_resolution',
        re_extraction: 're_extraction',
        obligation_analysis: 'obligation_analysis',
        score_aggregation: 'score_aggregation',
      };
      return {
        ...prev,
        stage: stageMap[nodeId] ?? prev.stage,
        stageMessage: msg.message ?? '',
        stepperNodes: updateStepper(prev.stepperNodes, nodeId, 'running'),
        eventLog: addLog(prev.eventLog, msg.message ?? `${nodeId} started`),
      };
    }

    case 'node_complete': {
      const nodeId = msg.node as string;
      const updated = updateStepper(prev.stepperNodes, nodeId, 'complete');
      const isDone = nodeId === 'score_aggregation';
      const newStats = { ...prev.nodeStats };
      if (typeof msg.done === 'number') {
        newStats[nodeId] = { done: msg.done, total: msg.total ?? msg.done, extra: msg.extra };
      }
      return {
        ...prev,
        stage: isDone ? 'done' : prev.stage,
        stageMessage: msg.message ?? '',
        stepperNodes: updated,
        eventLog: addLog(prev.eventLog, msg.message ?? `${nodeId} complete`),
        nodeStats: newStats,
      };
    }

    case 'area_processed':
      return {
        ...prev,
        areasProcessed: [...prev.areasProcessed, { area: msg.area, cached: !!msg.cached }],
        eventLog: addLog(prev.eventLog, `Area: ${msg.area}${msg.cached ? ' (cached)' : ''}`),
      };

    case 'dimension_extracted':
      return {
        ...prev,
        dimensionsExtracted: prev.dimensionsExtracted + (msg.count ?? 0),
        eventLog: addLog(
          prev.eventLog,
          `Extracted ${msg.count} obligations from ${String(msg.policy_id ?? '').slice(0, 8)}...`,
        ),
      };

    case 'comparison_started':
      return { ...prev, totalDimensions: msg.total_dimensions ?? 0 };

    case 'dimension_compared':
      return {
        ...prev,
        comparisonProgress: msg.progress ?? '',
        eventLog: addLog(
          prev.eventLog,
          `Compared ${msg.progress}: ${msg.matched ? 'matched' : 'gap found'}`,
        ),
      };

    case 'questions_ready':
      return {
        ...prev,
        stage: 'awaiting_hitl',
        questionsCount: msg.count ?? 0,
        stepperNodes: updateStepper(prev.stepperNodes, 'hitl', 'running'),
        eventLog: addLog(prev.eventLog, `${msg.count} questions need your input`),
      };

    case 'error':
      return {
        ...prev,
        stage: 'error',
        error: msg.message ?? 'Unknown error',
        eventLog: addLog(prev.eventLog, `Error: ${msg.message}`),
      };

    default:
      return prev;
  }
};

// ── Hook ───────────────────────────────────────────────────────────────────

export function useComplianceSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const [state, setState] = useState<ComplianceSocketState>({
    ...INITIAL_STATE,
    stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })),
  });

  const reset = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    setState({ ...INITIAL_STATE, stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })) });
  }, []);

  const connectWS = useCallback((url: string, onOpen?: (ws: WebSocket) => void) => {
    wsRef.current?.close();
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => { reconnectCountRef.current = 0; onOpen?.(ws); };
    ws.onmessage = (ev: MessageEvent) => {
      let msg: any;
      try { msg = JSON.parse(ev.data); } catch { return; }
      setState(prev => handleMessage(msg, prev));
    };
    ws.onerror = () => {
      setState(prev => {
        if (prev.sessionId) return prev;
        return { ...prev, stage: 'error', error: 'WebSocket connection failed.' };
      });
    };
    ws.onclose = () => { wsRef.current = null; };
  }, []);

  const start = useCallback(
    (companyId: string, policyIds: string[], token: string, jurisdiction?: string) => {
      setState({ ...INITIAL_STATE, stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })) });
      const url = `${WS_BASE}/api/v1/compliance/ws?token=${encodeURIComponent(token)}`;
      connectWS(url, (ws) => {
        ws.send(JSON.stringify({
          company_id: companyId,
          policy_ids: policyIds,
          jurisdiction: jurisdiction || '',
        }));
      });
    },
    [connectWS],
  );

  const resume = useCallback((sessionId: string, token: string) => {
    const url = `${WS_BASE}/api/v1/compliance/ws/resume?token=${encodeURIComponent(token)}`;
    setState(prev => ({
      ...prev,
      stage: 'obligation_analysis',
      stageMessage: 'Resuming assessment with your answers...',
      stepperNodes: updateStepper(
        updateStepper(prev.stepperNodes, 'hitl', 'complete'),
        'obligation_analysis', 'running',
      ),
    }));
    connectWS(url, (ws) => { ws.send(JSON.stringify({ session_id: sessionId })); });
  }, [connectWS]);

  const setQuestions = useCallback((questions: ComplianceQuestion[]) => {
    setState(prev => ({ ...prev, questions }));
  }, []);

  const setFinalResult = useCallback((result: ComplianceFinalResult) => {
    setState(prev => ({ ...prev, finalResult: result }));
  }, []);

  const disconnect = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  return { state, start, resume, reset, disconnect, setQuestions, setFinalResult };
}
